import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BAD_CAMERA_MOUNTING_MUTE_EVENT_DURATIONS } from '@app-assets/constants/assets.constants';
import {
  BadMountingCameraEvent,
  BadMountingCameraModelData,
  BadMountingCameraTabelClick,
  MuteEventDurationOption,
} from '@app-assets/models/assets.model';
import { AssetsService } from '@app-assets/services/assets.service';
import { DataService } from '@app-core/services/data/data.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';

import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-mute-bad-camera-mounting',
  templateUrl: './mute-bad-camera-mounting.component.html',
  styleUrls: ['./mute-bad-camera-mounting.component.scss'],
})
export class MuteBadCameraMountingComponent implements OnInit {
  public selectedButton: MuteEventDurationOption | null = null;
  public event: BadMountingCameraEvent;
  public mediaUrl: string = '';
  public hasReviewed: boolean;
  public buttonView: boolean;
  public rowView: boolean;
  public headdingText: string;
  public isActiveEvent: boolean = true;
  public currentDateFormat: string;
  public mutedDuration: string;
  public loader: boolean;
  public buttons = BAD_CAMERA_MOUNTING_MUTE_EVENT_DURATIONS;
  public durationButtonsGrouped: { [key: number]: MuteEventDurationOption[] } = {};
  public durationGroupKeys: string[] = [];

  private clickType: string | null = null;

  private ngUnsubscribeDateFormat: Subject<void> = new Subject();
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<MuteBadCameraMountingComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: BadMountingCameraModelData,
    public dataService: DataService,
    private assetsService: AssetsService,
    private snackbarService: SnackBarService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.groupButtonsByGroup();
    this.durationGroupKeys = Object.keys(this.durationButtonsGrouped);

    this.dataService._currentDateFormat.pipe(takeUntil(this.ngUnsubscribeDateFormat)).subscribe((value: string) => {
      if (value) {
        this.currentDateFormat = value;
      }
    });

    this.clickType = this.data.clickType || BadMountingCameraTabelClick.button;
    this.isActiveEvent = this.data.selectedTableType === 'activeEvents' ? true : false;
    this.headdingText = this.isActiveEvent ? 'Mute' : 'Unmute';
    this.event = this.data.event || ({} as BadMountingCameraEvent);
    this.mediaUrl = this.data.event.eventVideoFilename;

    if (this.clickType === BadMountingCameraTabelClick.row) {
      this.rowView = true;
      this.buttonView = false;
      this.hasReviewed = false;
    } else {
      this.rowView = false;
      this.buttonView = true;
      this.hasReviewed = true;
    }

    if (!this.isActiveEvent) {
      this.mutedDuration = this.getLabelByKey(this.event?.mutedForDurationInDays);
    }
  }

  private groupButtonsByGroup() {
    this.durationButtonsGrouped = this.buttons.reduce((acc, button) => {
      if (!acc[button.group]) {
        acc[button.group] = [];
      }
      acc[button.group].push(button);
      return acc;
    }, {});
  }

  private getLabelByKey(key: number): string | undefined {
    const item = this.buttons.find((d) => d.noOfDays === key);
    return item ? item.modelLabel ?? item.buttonLabel : undefined;
  }

  public selectButton(button) {
    this.selectedButton = button;
  }

  public enableSelectMuteDuration(navigateBack: boolean = false) {
    if (navigateBack) {
      this.hasReviewed = false;
    } else {
      this.hasReviewed = true;
    }
  }

  public onSubmit() {
    this.loader = true;
    const { deviceId } = this.event;
    const body = {
      eventType: this.event.eventType,
      ...(this.isActiveEvent ? { muteForDurationInDays: this.selectedButton.noOfDays } : {}),
      muteState: this.isActiveEvent ? true : false,
    };

    this.assetsService
      .updateMuteState(deviceId, body)
      .pipe(
        finalize(() => (this.loader = false)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          if (this.isActiveEvent) {
            const successMessage =
              this.translate.instant('mutedBadCameraMountingMuteEventSuccess') +
              this.translate.instant(this.selectedButton?.modelLabel || this.selectedButton?.buttonLabel);
            this.snackbarService.success(successMessage);
          } else {
            this.snackbarService.success(this.translate.instant('mutedBadCameraMountingUnmuteEventSuccess'));
          }
          this.dialogRef.close(true);
        },
        () => {
          if (this.isActiveEvent) {
            this.snackbarService.failure(this.translate.instant('mutedBadCameraMountingMutingFailed'));
          } else {
            this.snackbarService.failure(this.translate.instant('mutedBadCameraMountingUnmutingFailed'));
          }
        }
      );
  }
}
